import { deepCamelize, deepDecamelize } from '@/lib/caseTransform'
import { ApiError } from '@/services/api/ApiError'
import { refreshAuth } from '@/services/api/authRefresh'
import { beginKeySend, refuseKeySend, settleKeySend, type KeySend } from '@/services/api/idempotencySends'
import { OfflineError } from '@/services/api/OfflineError'
import { SessionChangedError } from '@/services/api/SessionChangedError'
import { SessionUnreachableError } from '@/services/api/SessionUnreachableError'
import { useAuthStore } from '@/store/authStore'
import { resolveImageUrls } from '@/utils/imageUrl'

// =============================================================================
// Core Request Client
// =============================================================================

const baseUrl = '/api/v1'
const timeout = 30000

// Keys an error payload may carry, in priority order. Each may hold a non-empty
// string or an array of strings (joined). `message` is string-only (below).
const ERROR_MESSAGE_KEYS = ['error', 'errors'] as const

function messageFromField(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value
  if (Array.isArray(value) && value.length > 0) return value.filter((x) => typeof x === 'string').join(', ')
  return undefined
}

// Normalize error payloads across the API. Most endpoints use `{ errors: [...] }`
// plural (array of validation messages), a few return `{ error: '...' }` singular,
// and some use `{ message: '...' }`. Return the first non-empty one we find.
function extractErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined
  const obj = data as Record<string, unknown>

  for (const key of ERROR_MESSAGE_KEYS) {
    const message = messageFromField(obj[key])
    if (message !== undefined) return message
  }
  return typeof obj.message === 'string' && obj.message.length > 0 ? obj.message : undefined
}

function getToken() {
  return useAuthStore.getState().token
}

function getUserId() {
  return useAuthStore.getState().user?.id
}

// An error the server answered a keyed write with definitively: it never will
// apply it (a 4xx other than a timeout or rate limit; 401s go through refresh).
function isDefinitiveError(status: number) {
  return status < 500 && status !== 408 && status !== 429
}

// The client speaks camelCase; the wire speaks snake_case. `body` accepts a
// plain object/array (decamelized + stringified here) as well as the classic
// pre-stringified JSON string (parsed, decamelized, re-stringified) and any raw
// `BodyInit` (FormData/Blob/…, passed straight through). Widening `body` beyond
// `BodyInit` is what lets domain fns hand the client an object.
type RequestBody = BodyInit | Record<string, unknown> | readonly unknown[] | null
type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: RequestBody
  idempotencyKey?: string
  /**
   * The endpoint answers 401 for a bad credential (wrong password, invalid
   * Google token), not an expired session: throw it as-is instead of
   * refreshing the session and retrying.
   */
  skipAuthRefresh?: boolean
  _isRetry?: boolean
  /** On a retry: the key's send record from before the attempt it retries. */
  _keySendBefore?: KeySend
}

// Decamelize a request body to the wire shape. Objects/arrays are decamelized
// and JSON-stringified; a pre-stringified JSON string is parsed, decamelized,
// and re-stringified (no-op on non-JSON strings); raw BodyInit (FormData, Blob,
// URLSearchParams, streams) passes through so uploads are never mangled.
function prepareBody(body: RequestBody | undefined): BodyInit | undefined {
  if (body === undefined || body === null) return undefined
  if (typeof body === 'string') {
    try {
      return JSON.stringify(deepDecamelize(JSON.parse(body)))
    } catch {
      return body
    }
  }
  if (Array.isArray(body) || Object.getPrototypeOf(body) === Object.prototype) {
    return JSON.stringify(deepDecamelize(body))
  }
  // FormData / Blob / URLSearchParams / ArrayBuffer views / ReadableStream, etc.
  return body as BodyInit
}

function buildHeaders(
  extra: HeadersInit | undefined,
  idempotencyKey: string | undefined,
  token: string | null,
): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extra as Record<string, string> | undefined),
  }

  if (token) headers['Authorization'] = `Bearer ${token}`
  if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey

  return headers
}

/** Parse an error body (camelized) and pull the best message out of it; falls back when the body isn't JSON. */
async function readError(
  response: Response,
  defaultMessage: string,
  fallbackOnUnparseable: string,
): Promise<{ message: string; data?: unknown }> {
  try {
    // Camelize the error body too, so consumers (conflictStore /
    // ConflictResolutionModal read `serverData.current.updatedAt`) never see
    // snake_case. `extractErrorMessage` keys (`errors`/`error`/`message`)
    // are single words and case-stable, so it still matches.
    const data = deepCamelize(await response.json())
    return { message: extractErrorMessage(data) || defaultMessage, data }
  } catch {
    return { message: fallbackOnUnparseable }
  }
}

/**
 * 401: recover the session and retry once. Only an explicit refusal of the
 * refresh token logs out — a network error / 5xx / timeout keeps the (expired)
 * session, and with it the offline queue, for the next attempt.
 *
 * `sentToken` is the bearer this request carried. If the store holds a
 * different one, an earlier refresh (or another tab) already rotated it — the
 * 401 is just late, so retry with the current token instead of starting a
 * second rotation that would revoke the token in-flight retries are using.
 *
 * A request is only ever retried as the user who sent it (`sentUserId`): if a
 * logout + login as someone else happened meanwhile, the new token is theirs.
 */
// Who a request went out as, and its key's send record from before it went.
type SentAs = { sentToken: string | null; sentUserId: number | undefined; keySendBefore: KeySend | undefined }

async function handleUnauthorized<T>(
  response: Response,
  endpoint: string,
  options: RequestOptions,
  { sentToken, sentUserId, keySendBefore }: SentAs,
): Promise<T> {
  // Parse the response body to get the actual error message
  const defaultMessage = 'Session expired. Please log in again.'
  const { message, data } = await readError(response, defaultMessage, defaultMessage)
  const unauthorized = new ApiError(message, 401, data)

  // Signed out (or never signed in), already retried, or a credential
  // rejection rather than a session problem: nothing to recover.
  const current = getToken()
  if (!current || options._isRetry || options.skipAuthRefresh) throw unauthorized

  const retry = () => {
    if (getUserId() !== sentUserId) throw new SessionChangedError()
    return request<T>(endpoint, { ...options, _isRetry: true, _keySendBefore: keySendBefore })
  }
  if (current !== sentToken) return retry()

  const result = await refreshAuth()
  if (result === 'refreshed') return retry()
  if (result === 'unreachable') {
    // The 401 came before the action ran: this attempt wasn't applied.
    refuseKeySend(options.idempotencyKey, keySendBefore)
    throw new SessionUnreachableError()
  }

  // A concurrent logout already cleaned up; don't wipe caches twice.
  if (getToken()) useAuthStore.getState().logout()
  throw unauthorized
}

type SendAttempt = { key: string | undefined; keySendBefore: KeySend | undefined; offlineAtSend: boolean }

/**
 * A network failure while the device is offline (`navigator.onLine` false is
 * reliable; true is not) is an `OfflineError`. If the device was already
 * offline when it was sent, it never left the device, so it isn't a send. A
 * timeout (AbortError) is neither.
 *
 * This trusts `navigator.onLine === false`. Where it lags a reconnect, or
 * reads false on a working network (some VPN/virtual-adapter setups), a
 * request that got out and lost its response is counted unsent; re-sending it
 * is only unsafe if the app is then closed and reopened over 24h later. A
 * timing guard was tried and dropped: measured on the main thread, it counted
 * writes that never left the device as sent.
 */
function offlineFailure(error: unknown, attempt: SendAttempt): unknown {
  if (!(error instanceof TypeError) || navigator.onLine) return error
  if (attempt.offlineAtSend) refuseKeySend(attempt.key, attempt.keySendBefore)
  return new OfflineError()
}

/**
 * fetch() rejects with a TypeError on genuine network failure, and the
 * AbortController fires a DOMException named 'AbortError' on timeout.
 * Anything else (e.g. a bug in resolveImageUrls or JSON parsing) is a real
 * error — rethrow it rather than mislabeling it as a network problem.
 */
function toApiError(error: unknown): unknown {
  if (error instanceof ApiError) return error
  if (error instanceof DOMException && error.name === 'AbortError') return new ApiError('Request timed out', 408)
  if (error instanceof TypeError) return new ApiError('Network error. Please check your connection.', 0)
  return error
}

export async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { idempotencyKey, skipAuthRefresh: _skipAuthRefresh, _isRetry, _keySendBefore, body, ...fetchOptions } = options
  // Never send a write's key as anyone but its owner, nor once the server may
  // have forgotten it (`idempotencySends.ts`).
  // A retry is part of the attempt it retries: a refusal puts back the record from before that attempt.
  const keySendBefore = _isRetry ? _keySendBefore : beginKeySend(idempotencyKey, getUserId())

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)
  const sentToken = getToken()
  const sentUserId = getUserId()
  const attempt = { key: idempotencyKey, keySendBefore, offlineAtSend: !navigator.onLine }

  try {
    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...fetchOptions,
      body: prepareBody(body),
      headers: buildHeaders(fetchOptions.headers, idempotencyKey, sentToken),
      signal: controller.signal,
    }).catch((error: unknown) => {
      throw offlineFailure(error, attempt)
    })

    // Handle 401 Unauthorized - try refresh before logging out
    if (response.status === 401) {
      return await handleUnauthorized<T>(response, endpoint, options, { sentToken, sentUserId, keySendBefore })
    }

    // Handle 204 No Content
    if (response.status === 204) {
      settleKeySend(idempotencyKey)
      return {} as T
    }

    // Handle error responses
    if (!response.ok) {
      const { message, data } = await readError(
        response,
        'An error occurred',
        response.statusText || 'An error occurred',
      )
      // Forget the key's send record only once the response is fully read.
      if (isDefinitiveError(response.status)) settleKeySend(idempotencyKey)
      // Rate-limited before the action ran: this attempt wasn't applied.
      if (response.status === 429) refuseKeySend(idempotencyKey, keySendBefore)
      throw new ApiError(message, response.status, data)
    }

    // Resolve image URLs on the raw snake_case body FIRST (it matches the wire
    // `image_url` field), THEN camelize so callers get `imageUrl`. Order chosen
    // deliberately: it keeps `utils/imageUrl.ts` (owned by another task)
    // unchanged while still matching, and the two passes commute.
    // The key's record is kept until the body is read: a 2xx whose body never
    // arrives is retried under the same owner and replay window.
    const json = resolveImageUrls(await response.json())
    settleKeySend(idempotencyKey)
    return deepCamelize<T>(json)
  } catch (error) {
    throw toApiError(error)
  } finally {
    clearTimeout(timeoutId)
  }
}

export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()

  // Param keys are app-side camelCase (e.g. `cardType`, `costMin`); the wire
  // expects snake_case (`card_type`, `cost_min`). Decamelize keys before
  // serializing. Idempotent on already-snake keys, so transitional call sites
  // still passing snake_case params keep working. Array values (serialized as
  // `key[]`) are preserved.
  const wireParams = deepDecamelize<Record<string, unknown>>(params)

  Object.entries(wireParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== '') {
          searchParams.append(`${key}[]`, String(v))
        }
      })
    } else {
      searchParams.append(key, String(value))
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}
